
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

// Global flag to prevent multiple handlers from running simultaneously
let handlerRunning = false;

export const DeviceAssignmentHandler = () => {
  const queryClient = useQueryClient();
  const hasProcessed = useRef(false);
  const { user, session } = useAuth();

  useEffect(() => {
    console.log("🔍 DeviceAssignmentHandler useEffect triggered, hasProcessed:", hasProcessed.current);
    
    // Don't run if no session/user or if already processed
    if (!session || !user || hasProcessed.current || handlerRunning) {
      console.log("⏭️ Handler skipped - no session, already processed, or running");
      return;
    }

    const handleDeviceAssignments = async () => {
      try {
        handlerRunning = true;
        hasProcessed.current = true;

        console.log("👤 Processing assignments for user:", user.email);
        
        // Check for pending invitations FIRST, also check recently accepted ones as fallback
        console.log("🔎 Checking pending invitations for:", user.email);
        
        // Calculate timestamp for 60 seconds ago (for fallback check)
        const recentlyAcceptedThreshold = new Date(Date.now() - 60000).toISOString();
        
        const { data: pendingInvitations, error: pendingInvitationError } = await supabase
          .from('user_invitations')
          .select('*')
          .eq('email', user.email)
          .or(`status.eq.pending,and(status.eq.accepted,accepted_at.gt.${recentlyAcceptedThreshold})`)
          .gt('expires_at', new Date().toISOString());

        if (pendingInvitationError) {
          console.error("❌ Error fetching pending invitations:", pendingInvitationError);
          return;
        }
        
        console.log("📨 Pending/recently accepted invitations found:", pendingInvitations?.length || 0);

        // Check if user already has roles
        console.log("🔍 Checking if user already has roles");
        const { data: existingRoles, error: roleError } = await supabase
          .from('user_roles')
          .select('role, organization_id')
          .eq('user_id', user.id);

        if (roleError) {
          console.error("❌ Error checking existing roles:", roleError);
          return;
        }

        // Only exit early if user has roles AND no pending invitations
        if (existingRoles && existingRoles.length > 0 && 
            (!pendingInvitations || pendingInvitations.length === 0)) {
          console.log("✅ User already has roles and no pending invitations - skipping");
          return;
        }

        console.log("🔧 DeviceAssignmentHandler: Processing assignments", {
          hasRoles: existingRoles && existingRoles.length > 0,
          hasPendingInvitations: pendingInvitations && pendingInvitations.length > 0
        });

        // Check for session storage data from invitation link FIRST
        const contactId = sessionStorage.getItem("link_contact_id");
        const invitationToken = sessionStorage.getItem("invitation_token");
        const invitationOrgId = sessionStorage.getItem("invitation_organization_id");

        let actualChanges = false;

        if (contactId && invitationToken && invitationOrgId) {
          console.log("🔗 Processing invitation link data");
          
          try {
            const { error: processError } = await supabase.rpc('process_invitation_acceptance', {
              invitation_id: invitationToken,
              accepting_user_id: user.id
            });

            if (processError) {
              console.error("❌ Error processing invitation acceptance:", processError);
            } else {
              console.log("✅ Successfully processed invitation acceptance");
              toast.success("Welcome! Your invitation has been accepted and your profile has been linked successfully");
              
              sessionStorage.removeItem("link_contact_id");
              sessionStorage.removeItem("invitation_token");
              sessionStorage.removeItem("invitation_organization_id");
              
              actualChanges = true;
            }
          } catch (error) {
            console.error("❌ Error in invitation acceptance:", error);
            toast.error("Failed to process invitation");
          }
        }

        // Removed automatic organization creation - organizations should only be created via CRM
        console.log("ℹ️ Skipping automatic organization creation - organizations managed via CRM only");

        // Pending invitations already checked above
        if (!pendingInvitations || pendingInvitations.length === 0) {
          console.log("ℹ️ No pending invitations to process");
          
          // ONLY invalidate if we made actual changes
          if (actualChanges) {
            console.log("🔄 Invalidating queries due to actual changes made");
            queryClient.invalidateQueries({ queryKey: ["user-role"] });
          }
          return;
        }

        // Process pending invitations
        for (const invitation of pendingInvitations) {
          console.log("📝 Processing invitation:", invitation.id, "Status:", invitation.status);
          
          // Handle device assignments if they exist
          if (invitation.device_ids && invitation.device_ids.length > 0) {
            try {
              console.log("🗑️ Removing existing assignments for devices:", invitation.device_ids);
              const { error: deleteError } = await supabase
                .from('device_assignments')
                .delete()
                .eq('user_id', user.id)
                .in('device_id', invitation.device_ids);

              if (deleteError) {
                console.error("❌ Error deleting existing assignments:", deleteError);
                continue;
              }

              console.log("➕ Creating new device assignments");
              const deviceAssignments = invitation.device_ids.map((deviceId: string) => ({
                user_id: user.id,
                device_id: deviceId
              }));

              const { error: assignmentError } = await supabase
                .from('device_assignments')
                .insert(deviceAssignments);

              if (assignmentError) {
                console.error("❌ Error creating assignments:", assignmentError);
                continue;
              }

              actualChanges = true;
            } catch (error) {
              console.error("❌ Error processing device assignments:", error);
              continue;
            }
          }

          // Check if user already has a role in this organization
          console.log("🔍 Checking existing role for organization:", invitation.organization_id);
          const { data: existingRole } = await supabase
            .from('user_roles')
            .select()
            .eq('user_id', user.id)
            .eq('organization_id', invitation.organization_id)
            .maybeSingle();

          if (!existingRole) {
            console.log("👥 Creating user role for organization:", invitation.organization_id, "with role:", invitation.invited_role);
            const { error: roleError } = await supabase
              .from('user_roles')
              .insert([{
                user_id: user.id,
                organization_id: invitation.organization_id,
                role: invitation.invited_role || 'standard'
              }]);

            if (roleError) {
              console.error("❌ Error creating user role:", roleError);
            } else {
              actualChanges = true;
            }
          }

          // Update the invitation status to processed
          console.log("📋 Updating invitation status to processed");
          const { error: updateStatusError } = await supabase
            .from('user_invitations')
            .update({ status: 'processed' })
            .eq('id', invitation.id)
            .eq('email', user.email);

          if (updateStatusError) {
            console.error("❌ Error updating invitation status:", updateStatusError);
          } else {
            console.log("✅ Successfully processed invitation:", invitation.id);
            actualChanges = true;
          }
        }

        // CRITICAL: Only invalidate queries if we actually made changes
        if (actualChanges) {
          console.log("🔄 Invalidating specific queries due to actual changes made");
          queryClient.invalidateQueries({ queryKey: ["user-devices"] });
          queryClient.invalidateQueries({ queryKey: ["user-role"] });
          toast.success("Devices assigned successfully");
        } else {
          console.log("ℹ️ No actual changes were made - not invalidating any queries");
        }

      } catch (error) {
        console.error("❌ Error in device assignment handler:", error);
      } finally {
        handlerRunning = false;
      }
    };

    // Defer the handler to avoid potential auth conflicts
    setTimeout(() => {
      handleDeviceAssignments();
    }, 100);
  }, [user, session, queryClient]);

  console.log("🎯 DeviceAssignmentHandler rendered");
  return null;
};
